import useMenu from './useMenu'
import * as R from 'ramda'
import type { ManifestDetail } from '@/typings/bill'
import { getProductCodeByUrl } from '@/utils/common'
import {
  DEFAULT_MENU_NAME,
  resolveMenuName,
  resolveTabKey,
} from '@/utils/routeMenu'

const { domain } = getProductCodeByUrl()
const TabKey = `${domain.toUpperCase()}_TAB_INFO__`
const ActiveTabKey = `${domain.toUpperCase()}_ACTIVE_TAB_INFO__`

type TabDetail = { key: any; value: Partial<ManifestDetail>; route: any }
type MenuTabGroup = {
  activeKey: string
  tabs: TabDetail[]
}
type LegacyTabsList = Record<string, MenuTabGroup | TabDetail[]>
type TabsList = Record<string, MenuTabGroup>
const DefaultTabStorageScope = 'default'
const tabStorageScope = ref(DefaultTabStorageScope)

function cloneDefaultValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function getScopedStorageKey(key: string, scope = tabStorageScope.value) {
  return `${key}$${scope || DefaultTabStorageScope}`
}

function readScopedStorage<T>(key: string, defaultValue: T) {
  try {
    const rawValue = window.localStorage.getItem(getScopedStorageKey(key))
    return rawValue
      ? (JSON.parse(rawValue) as T)
      : cloneDefaultValue(defaultValue)
  } catch {
    return cloneDefaultValue(defaultValue)
  }
}

function useScopedLocalStorage<T>(key: string, defaultValue: T) {
  const data = ref(readScopedStorage(key, defaultValue)) as Ref<T>
  let scopedKey = getScopedStorageKey(key)

  watch(
    data,
    (value) => {
      window.localStorage.setItem(scopedKey, JSON.stringify(value))
    },
    { deep: true },
  )

  watch(
    tabStorageScope,
    () => {
      scopedKey = getScopedStorageKey(key)
      data.value = readScopedStorage(key, defaultValue)
    },
    { flush: 'sync' },
  )

  return data
}

export function setTabStorageScope(scope?: string) {
  const nextScope = scope?.trim() || DefaultTabStorageScope
  if (tabStorageScope.value === nextScope) return

  tabStorageScope.value = nextScope
}

export function removeTabStorageScope(scope?: string) {
  const targetScope = scope?.trim()
  if (!targetScope) return

  window.localStorage.removeItem(getScopedStorageKey(TabKey, targetScope))
  window.localStorage.removeItem(getScopedStorageKey(ActiveTabKey, targetScope))
}

const tabs = useScopedLocalStorage<LegacyTabsList>(TabKey, {})
const legacyActiveTabName = useScopedLocalStorage(
  ActiveTabKey,
  DEFAULT_MENU_NAME,
)

const normalizeTabsCache = () => {
  const normalized: TabsList = {}
  Object.entries(tabs.value).forEach(([menuName, group]) => {
    const normalizeTab = (item: TabDetail) => ({
      ...item,
      key: String(item.key).includes('_')
        ? item.key
        : resolveTabKey(item.route),
    })

    if (Array.isArray(group)) {
      const normalizedTabs = group.map(normalizeTab)
      const activeTab = normalizedTabs.find(
        (item) =>
          item.key === legacyActiveTabName.value ||
          String(item.key).endsWith(`_${legacyActiveTabName.value}`),
      )
      normalized[menuName] = {
        activeKey: activeTab?.key || '',
        tabs: normalizedTabs,
      }
      return
    }

    const normalizedTabs = Array.isArray(group?.tabs)
      ? group.tabs.map(normalizeTab)
      : []
    const activeTab = normalizedTabs.find(
      (item) =>
        item.key === group?.activeKey ||
        String(item.key).endsWith(`_${group?.activeKey}`),
    )
    normalized[menuName] = {
      activeKey: activeTab?.key || '',
      tabs: normalizedTabs,
    }
  })
  tabs.value = normalized
}

normalizeTabsCache()

export default () => {
  const { activeMenuName } = useMenu()
  const router = useRouter()
  const route = useRoute()

  const ensureGroup = (menuName = activeMenuName.value): MenuTabGroup => {
    const group = tabs.value[menuName]
    if (!group) {
      tabs.value[menuName] = {
        activeKey: '',
        tabs: [],
      }
    }
    return tabs.value[menuName]
  }

  const getTargetMenuName = (routeConfig?: any) => {
    return resolveMenuName(routeConfig || route, activeMenuName.value as any)
  }

  const getCurrentTabList = (menuName = activeMenuName.value) => {
    return ensureGroup(menuName).tabs
  }

  const getCurrentRouteName = (name?: string) => name || route.name

  const activeKey = computed(() => ensureGroup().activeKey)

  const activeTabName = computed({
    get: () => activeKey.value || activeMenuName.value,
    set: (value: string) => {
      const group = ensureGroup()
      group.activeKey = value === activeMenuName.value ? '' : value
      legacyActiveTabName.value = value
    },
  })

  const activateMenu = (menuName: string) => {
    activeMenuName.value = menuName
  }

  const activateFixedTab = (menuName = activeMenuName.value) => {
    const group = ensureGroup(menuName)
    group.activeKey = ''
    legacyActiveTabName.value = menuName
  }

  const activateTab = (menuName: string, key: string) => {
    const group = ensureGroup(menuName)
    group.activeKey = key
    legacyActiveTabName.value = key
  }

  const findTabIndex = (key?: string, name?: string, menuName?: string) => {
    const targetMenuName = menuName || activeMenuName.value
    const currentRouteName = getCurrentRouteName(name)
    const currentKey = key || ensureGroup(targetMenuName).activeKey
    return getCurrentTabList(targetMenuName).findIndex(
      (item) =>
        item.route.name === currentRouteName &&
        (item.key === currentKey ||
          (key && String(item.key).endsWith(`_${currentKey}`))),
    )
  }

  const currentTabs = computed<TabDetail[]>(() => {
    return ensureGroup().tabs
  })

  const ensureTab = ({
    key,
    value,
    route,
  }: {
    key: string
    value: Partial<ManifestDetail>
    route: any
  }) => {
    const normalizedKey = String(key).includes('_') ? key : resolveTabKey(route)
    const menuName = getTargetMenuName(route)
    activateMenu(menuName)
    activateTab(menuName, normalizedKey)
    const currentTabList = getCurrentTabList(menuName)

    const cache = currentTabList.find(
      (item) => item.key === normalizedKey && item.route.name === route.name,
    )

    if (!cache) {
      currentTabList.push({
        value,
        route,
        key: normalizedKey,
      })
    }
  }

  const addTab = ({
    value,
    route,
    callback,
  }: {
    value: Partial<ManifestDetail>
    route: any
    callback?: () => any
  }) => {
    const key = resolveTabKey(route)
    ensureTab({
      value,
      route,
      key,
    })
    if (callback && !callback()) return
    router.push(route)
  }

  const addMultiTab = (list: TabDetail[]) => {
    list.forEach((item) => {
      const key =
        item.key && String(item.key).includes('_')
          ? item.key
          : resolveTabKey(item.route)
      ensureTab({
        ...item,
        key,
      })
    })
    const lastTab = list[list.length - 1]
    if (lastTab) {
      router.push(lastTab.route)
    }
  }

  const getTab = (key: string, name?: string, routeConfig?: any): TabDetail => {
    const menuName = routeConfig
      ? getTargetMenuName(routeConfig)
      : activeMenuName.value
    const currentRouteName = getCurrentRouteName(name)
    const tab = getCurrentTabList(menuName).find(
      (item) =>
        item.route.name === currentRouteName &&
        (item.key === key || String(item.key).endsWith(`_${key}`)),
    )
    return tab as TabDetail
  }

  const setTab = (value: Partial<TabDetail>, key?: string, name?: string) => {
    const index = findTabIndex(key, name)
    if (index === -1) return
    const currentTabList = getCurrentTabList()
    currentTabList[index] = R.mergeDeepRight(currentTabList[index], value)
  }

  const removeTab = ({
    key = activeTabName.value,
    name,
    routeConfig,
    beforeRemove,
    afterRemove,
  }: {
    key?: string
    name?: string
    routeConfig?: any
    beforeRemove?: () => boolean
    afterRemove?: () => boolean
  } = {}) => {
    if (beforeRemove && !beforeRemove()) return
    const removeIndex = findTabIndex(key, name)
    if (removeIndex === -1) return

    const currentTabList = getCurrentTabList()
    const isActiveTab = currentTabList[removeIndex]?.key === activeKey.value
    currentTabList.splice(removeIndex, 1)
    if (isActiveTab && currentTabList.length) {
      const nextIndex = Math.min(removeIndex, currentTabList.length - 1)
      const nextTab = currentTabList[nextIndex]
      activateTab(activeMenuName.value, nextTab.key)
      if (afterRemove && !afterRemove()) return
      router.push(nextTab.route)
      return
    }

    activateFixedTab()
    if (afterRemove && !afterRemove()) return
    router.push({ name: activeMenuName.value, ...routeConfig })
  }

  const hasTab = (key: string = '') => {
    if (!key) return false
    return getCurrentTabList().findIndex((item) => item.key === key) !== -1
  }

  const changeTab = ({
    key,
    name,
    beforeChange,
    afterChange,
  }: {
    key?: string
    name?: string
    beforeChange?: () => boolean
    afterChange?: () => boolean
  } = {}) => {
    if (beforeChange && !beforeChange()) return
    const cache = getTab(key as string, name as string)
    if (cache) {
      activateTab(activeMenuName.value, key as string)
      router.push(cache.route)
    } else {
      activateFixedTab()
      router.push({ name: activeMenuName.value as string })
    }
    if (afterChange && !afterChange()) return
  }

  const replaceTab = ({
    key,
    name,
    routeConfig,
    value,
  }: {
    key?: string
    name?: string
    routeConfig: any
    value: any
  }) => {
    const cache = getTab(key as string, name as string, routeConfig)
    if (!cache) return
    cache.route = routeConfig
    cache.value = R.mergeDeepRight(cache.value, value)
    router.replace(routeConfig)
  }

  const clearTab = () => {
    tabs.value = {}
    activeMenuName.value = DEFAULT_MENU_NAME
    legacyActiveTabName.value = DEFAULT_MENU_NAME
  }

  const syncTabByRoute = (routeConfig: any = route) => {
    const menuName = getTargetMenuName(routeConfig)
    activateMenu(menuName)

    const currentRouteName = routeConfig.name as string
    if (!currentRouteName || currentRouteName === menuName) {
      activateFixedTab(menuName)
      return
    }

    const key = resolveTabKey(routeConfig)
    const cache = getTab(key, currentRouteName, routeConfig)
    if (cache) {
      activateTab(menuName, cache.key)
      return
    }

    if (routeConfig.params?.id) {
      ensureTab({
        key,
        value: {},
        route: {
          name: currentRouteName,
          params: routeConfig.params,
          query: routeConfig.query,
          meta: routeConfig.meta,
        },
      })
    }
  }

  return {
    activeTabName,
    activeKey,
    currentTabs,
    addTab,
    addMultiTab,
    ensureTab,
    setTab,
    getTab,
    removeTab,
    hasTab,
    changeTab,
    clearTab,
    syncTabByRoute,
    replaceTab,
    tabs,
  }
}
